import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Users,
  MessageSquare,
  Search,
  TrendingUp,
  Eye,
  Trash2,
  ThumbsUp,
  Database,
  Shield,
  Home,
} from "lucide-react";
import axios from "axios";
import { config } from "../config";
import FeedbackAnalytics from "./FeedbackAnalytics";
import ThemeToggle from "./ThemeToggle";
import UserMenu from "./UserMenu";
import TicketManagement from "./TicketManagement";
import ZohoOAuthManager from "./ZohoOAuthManager";

interface AdminStats {
  user_stats: {
    total_users: number;
    active_users: number;
    inactive_users: number;
  };
  conversation_stats: {
    total_conversations: number;
    total_messages: number;
    avg_messages_per_conversation: number;
  };
  recent_activity: {
    new_conversations_7_days: number;
    new_messages_7_days: number;
  };
  top_users: Array<{
    username: string;
    email: string;
    conversation_count: number;
  }>;
}

interface Conversation {
  thread_id: string;
  title: string;
  message_count: number;
  created_at: string;
  last_message_at: string;
  user_info: {
    user_id: string;
    username: string;
    email: string;
    full_name?: string;
  };
  latest_message_preview?: string;
  latest_message_role?: string;
}

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchIn, setSearchIn] = useState("content");

  const api = useMemo(
    () =>
      axios.create({
        baseURL: config.API_URL,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      }),
    []
  );

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      // Update token in case it changed
      const token = localStorage.getItem("access_token");
      console.log(
        "Fetching stats with token:",
        token ? "Token present" : "No token"
      );
      api.defaults.headers.Authorization = `Bearer ${token}`;
      const response = await api.get("/admin/stats");
      console.log("Stats response:", response.data);
      setStats(response.data);
    } catch (error: any) {
      console.error("Error fetching stats:", error);
      console.error("Error response:", error.response);
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.error(
          "Authentication failed - user may not have admin privileges"
        );
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      // Update token in case it changed
      const token = localStorage.getItem("access_token");
      console.log(
        "Fetching conversations with token:",
        token ? "Token present" : "No token"
      );
      api.defaults.headers.Authorization = `Bearer ${token}`;
      const response = await api.get("/admin/conversations");
      console.log("Conversations response:", response.data);
      setConversations(response.data);
    } catch (error: any) {
      console.error("Error fetching conversations:", error);
      console.error("Error response:", error.response);
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.error(
          "Authentication failed - user may not have admin privileges"
        );
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (activeTab === "dashboard") {
      fetchStats();
    } else if (activeTab === "conversations") {
      fetchConversations();
    }
  }, [activeTab, fetchStats, fetchConversations]);

  // Check if user is admin
  if (!user?.is_superuser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-yellow-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 flex items-center justify-center">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-200/30 to-yellow-200/30 dark:from-orange-800/20 dark:to-yellow-800/20 rounded-full blur-3xl animate-float"></div>
          <div
            className="absolute top-1/2 -left-40 w-96 h-96 bg-gradient-to-br from-red-200/20 to-orange-200/20 dark:from-red-800/15 dark:to-orange-800/15 rounded-full blur-3xl animate-float"
            style={{ animationDelay: "2s" }}
          ></div>
        </div>

        <div className="relative z-10 text-center p-8 bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-2xl shadow-soft border border-orange-200/50 dark:border-neutral-700/50 max-w-md">
          <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <TrendingUp size={24} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">
            Access Denied
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            You need admin privileges to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-xl shadow-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-200 transform hover:scale-105"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    try {
      setLoading(true);
      // Update token in case it changed
      api.defaults.headers.Authorization = `Bearer ${localStorage.getItem(
        "access_token"
      )}`;
      const response = await api.post("/admin/search", {
        search_term: searchTerm,
        search_in: searchIn,
        limit: 50,
      });
      setConversations(response.data.results);
    } catch (error: any) {
      console.error("Error searching:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.error(
          "Authentication failed - user may not have admin privileges"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (threadId: string) => {
    if (!window.confirm("Are you sure you want to delete this conversation?"))
      return;

    try {
      // Update token in case it changed
      api.defaults.headers.Authorization = `Bearer ${localStorage.getItem(
        "access_token"
      )}`;
      await api.delete(`/admin/conversations/${threadId}`);
      fetchConversations(); // Refresh list
    } catch (error: any) {
      console.error("Error deleting conversation:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.error(
          "Authentication failed - user may not have admin privileges"
        );
      }
    }
  };

  const formatDate = (dateString: string) => {
    return (
      new Date(dateString).toLocaleDateString() +
      " " +
      new Date(dateString).toLocaleTimeString()
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-yellow-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient orbs */}
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-200/30 to-yellow-200/30 dark:from-orange-800/20 dark:to-yellow-800/20 rounded-full blur-3xl animate-float"></div>
        <div
          className="absolute top-1/2 -left-40 w-96 h-96 bg-gradient-to-br from-red-200/20 to-orange-200/20 dark:from-red-800/15 dark:to-orange-800/15 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "2s" }}
        ></div>
        <div
          className="absolute -bottom-40 right-1/3 w-64 h-64 bg-gradient-to-br from-yellow-200/25 to-orange-200/25 dark:from-yellow-800/15 dark:to-orange-800/15 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "4s" }}
        ></div>
      </div>

      {/* Header */}
      <div className="relative z-50 bg-white/80 dark:bg-neutral-800/80 backdrop-blur-xl border-b border-orange-200/50 dark:border-neutral-700/50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                <TrendingUp size={22} className="text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                  Admin Panel
                </h1>
                <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                  System Management Dashboard
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Home Button */}
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-all duration-200"
                title="Back to Home"
              >
                <Home size={16} />
                Home
              </button>

              {/* User Menu */}
              <UserMenu />

              {/* Theme Toggle */}
              <ThemeToggle variant="tab" size="sm" />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab("dashboard")}
            className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 ${
              activeTab === "dashboard"
                ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg"
                : "bg-white/80 dark:bg-neutral-800/80 text-neutral-700 dark:text-neutral-200 hover:bg-white dark:hover:bg-neutral-800 shadow-soft border border-orange-200/50 dark:border-neutral-700/50"
            }`}
          >
            <TrendingUp size={20} className="mr-2" />
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab("conversations")}
            className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 ${
              activeTab === "conversations"
                ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg"
                : "bg-white/80 dark:bg-neutral-800/80 text-neutral-700 dark:text-neutral-200 hover:bg-white dark:hover:bg-neutral-800 shadow-soft border border-orange-200/50 dark:border-neutral-700/50"
            }`}
          >
            <MessageSquare size={20} className="mr-2" />
            Conversations
          </button>
          <button
            onClick={() => setActiveTab("tickets")}
            className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 ${
              activeTab === "tickets"
                ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg"
                : "bg-white/80 dark:bg-neutral-800/80 text-neutral-700 dark:text-neutral-200 hover:bg-white dark:hover:bg-neutral-800 shadow-soft border border-orange-200/50 dark:border-neutral-700/50"
            }`}
          >
            <Database size={20} className="mr-2" />
            Tickets
          </button>
          <button
            onClick={() => setActiveTab("oauth")}
            className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 ${
              activeTab === "oauth"
                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg"
                : "bg-white/80 dark:bg-neutral-800/80 text-neutral-700 dark:text-neutral-200 hover:bg-white dark:hover:bg-neutral-800 shadow-soft border border-blue-200/50 dark:border-neutral-700/50"
            }`}
          >
            <Shield size={20} className="mr-2" />
            OAuth
          </button>
          <button
            onClick={() => setActiveTab("feedback")}
            className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 ${
              activeTab === "feedback"
                ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg"
                : "bg-white/80 dark:bg-neutral-800/80 text-neutral-700 dark:text-neutral-200 hover:bg-white dark:hover:bg-neutral-800 shadow-soft border border-orange-200/50 dark:border-neutral-700/50"
            }`}
          >
            <ThumbsUp size={20} className="mr-2" />
            Feedback Analytics
          </button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div>
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="flex items-center gap-3 p-6 bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-2xl shadow-soft">
                  <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-neutral-600 dark:text-neutral-300 font-medium">
                    Loading dashboard...
                  </span>
                </div>
              </div>
            ) : stats ? (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-2xl shadow-soft border border-orange-200/50 dark:border-neutral-700/50 p-6 hover:shadow-medium transition-all duration-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                          Total Users
                        </p>
                        <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                          {stats.user_stats.total_users}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                        <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-2xl shadow-soft border border-orange-200/50 dark:border-neutral-700/50 p-6 hover:shadow-medium transition-all duration-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                          Total Conversations
                        </p>
                        <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                          {stats.conversation_stats.total_conversations}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                        <MessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-2xl shadow-soft border border-orange-200/50 dark:border-neutral-700/50 p-6 hover:shadow-medium transition-all duration-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                          Total Messages
                        </p>
                        <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                          {stats.conversation_stats.total_messages}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                        <MessageSquare className="w-6 h-6 text-green-600 dark:text-green-400" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-2xl shadow-soft border border-orange-200/50 dark:border-neutral-700/50 p-6 hover:shadow-medium transition-all duration-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                          Avg Messages/Conv
                        </p>
                        <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                          {stats.conversation_stats.avg_messages_per_conversation.toFixed(
                            1
                          )}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top Users */}
                <div className="bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-2xl shadow-soft border border-orange-200/50 dark:border-neutral-700/50 p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-orange-500" />
                    Most Active Users
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-neutral-200 dark:border-neutral-700">
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wider">
                            Conversations
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700/50">
                        {stats.top_users.map((user, index) => (
                          <tr
                            key={index}
                            className="hover:bg-neutral-50 dark:hover:bg-neutral-700/30 transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900 dark:text-neutral-100">
                              {user.username}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600 dark:text-neutral-400">
                              {user.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600 dark:text-neutral-400">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                                {user.conversation_count}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center h-64">
                <div className="text-center p-6 bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-2xl shadow-soft">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <p className="text-neutral-600 dark:text-neutral-400">
                    Failed to load dashboard stats
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Conversations Tab */}
        {activeTab === "conversations" && (
          <div>
            {/* Search */}
            <div className="bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-2xl shadow-soft border border-orange-200/50 dark:border-neutral-700/50 p-6 mb-6">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-3 bg-white/80 dark:bg-neutral-800/80 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 placeholder-neutral-400 dark:placeholder-neutral-500 text-neutral-700 dark:text-neutral-200"
                  />
                </div>
                <select
                  value={searchIn}
                  onChange={(e) => setSearchIn(e.target.value)}
                  className="px-4 py-3 bg-white/80 dark:bg-neutral-800/80 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-neutral-700 dark:text-neutral-200"
                >
                  <option value="content">Content</option>
                  <option value="title">Title</option>
                  <option value="user">User</option>
                </select>
                <button
                  onClick={handleSearch}
                  className="flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-xl shadow-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-200 transform hover:scale-105"
                >
                  <Search size={20} className="mr-2" />
                  Search
                </button>
                <button
                  onClick={fetchConversations}
                  className="px-6 py-3 bg-white/80 dark:bg-neutral-800/80 text-neutral-700 dark:text-neutral-200 font-medium rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 hover:bg-white dark:hover:bg-neutral-800 transition-all duration-200 transform hover:scale-105"
                >
                  Show All
                </button>
              </div>
            </div>

            {/* Conversations List */}
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="flex items-center gap-3 p-6 bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-2xl shadow-soft">
                  <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-neutral-600 dark:text-neutral-300 font-medium">
                    Loading conversations...
                  </span>
                </div>
              </div>
            ) : (
              <div className="bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-2xl shadow-soft border border-orange-200/50 dark:border-neutral-700/50 overflow-hidden">
                <ul className="divide-y divide-neutral-200 dark:divide-neutral-700">
                  {conversations.map((conversation) => (
                    <li
                      key={conversation.thread_id}
                      className="px-6 py-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/30 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                              {conversation.title}
                            </h3>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() =>
                                  window.open(
                                    `/admin/conversations/${conversation.thread_id}`,
                                    "_blank"
                                  )
                                }
                                className="p-2 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/30 rounded-lg transition-all duration-200"
                                title="View Details"
                              >
                                <Eye size={18} />
                              </button>
                              <button
                                onClick={() =>
                                  deleteConversation(conversation.thread_id)
                                }
                                className="p-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200"
                                title="Delete Conversation"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center text-sm text-neutral-600 dark:text-neutral-400">
                            <span className="font-medium text-neutral-900 dark:text-neutral-100">
                              {conversation.user_info.username}
                            </span>
                            <span className="mx-2">•</span>
                            <span>{conversation.user_info.email}</span>
                            <span className="mx-2">•</span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                              {conversation.message_count} messages
                            </span>
                            <span className="mx-2">•</span>
                            <span>
                              {formatDate(conversation.last_message_at)}
                            </span>
                          </div>
                          {conversation.latest_message_preview && (
                            <div className="mt-3 p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                              <div className="text-sm text-neutral-700 dark:text-neutral-300">
                                <span className="font-medium text-orange-600 dark:text-orange-400">
                                  {conversation.latest_message_role === "user"
                                    ? "User: "
                                    : "AI: "}
                                </span>
                                {conversation.latest_message_preview}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Tickets Tab */}
        {activeTab === "tickets" && (
          <div>
            <TicketManagement />
          </div>
        )}

        {/* OAuth Tab */}
        {activeTab === "oauth" && (
          <div>
            <ZohoOAuthManager />
          </div>
        )}

        {/* Feedback Analytics Tab */}
        {activeTab === "feedback" && (
          <div>
            <FeedbackAnalytics />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
